import { Modal } from 'antd';
import { usd } from '../../utilities/format';
import { t } from '../../i18n/i18n';
import { platforms } from './data';
import './PurchaseReviewDialog.scss';


const platformName = (id) => platforms.find((p) => p.id === id)?.label ?? id ?? '—';

const yesNo = (value) => (value ? t('Yes') : t('No'));

/** label / value pairs in one panel — a `<dl>`, so each value is tied to its label. */
const Group = ({ title, rows }) => (
  <section className="pr-dialog__group" aria-label={title}>
    <h3 className="pr-dialog__group-title">{title}</h3>
    <dl className="pr-dialog__rows">
      {rows.map(([label, value]) => (
        <div key={label} className="pr-dialog__row">
          <dt className="pr-dialog__label">{label}</dt>
          <dd className="pr-dialog__value">{value}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export default function PurchaseReviewDialog({
  open,
  preview,
  error,
  isSubmitting,
  onCancel,
  onConfirm,
}) {
  const price = usd(preview?.final_price, { decimals: 2 });
  // A free trial has nothing to pay, so the button can't promise a charge.
  const isFree = Number(preview?.final_price) === 0;

  return (
    <Modal
      open={open}
      onCancel={isSubmitting ? undefined : onCancel}
      footer={null}
      centered
      width={520}
      mask={{ closable: !isSubmitting }}
      destroyOnHidden
      className="pr-dialog"
      rootClassName="pr-dialog-root"
      aria-label={t('Purchase review')}
    >
      <div className="pr-dialog__body">
        <header className="pr-dialog__head">
          <h2 className="pr-dialog__title">{t('Purchase Review')}</h2>
          <p className="pr-dialog__lead">
            {t('Check your order before you continue. Confirm takes you to the secure payment page.')}
          </p>
        </header>

        {preview && (
          <>
            <Group
              title={t('Account')}
              rows={[
                [t('Account Size'), usd(preview.account_size, { decimals: 2 })],
                [t('Plan Type'), preview.plan_type ?? '—'],
                [t('Platform'), platformName(preview.platform)],
              ]}
            />

            <Group
              title={t('Trading conditions')}
              rows={[
                [t('Spread Type'), preview.raw_spread ? t('Raw spread') : t('Standard spread')],
                [t('Payout Type'), t(preview.payout_type) ?? '—'],
                [t('Swap Free'), yesNo(preview.swap_free)],
                [t('Profit Split'), `${preview.profit_split}%`],
              ]}
            />

            {/* The total is the one figure the trader is agreeing to, so it sits
                apart from the specs; a coupon shows only when there is one —
                "Coupon Applied: No / Coupon Name: N/A" was two rows of nothing. */}
            <section className="pr-dialog__total" aria-label={t('Total')}>
              {preview.coupon_applied && (
                <div className="pr-dialog__row">
                  <span className="pr-dialog__label">{t('Coupon Applied')}</span>
                  <span className="pr-dialog__coupon">{preview.coupon_name ?? t('Yes')}</span>
                </div>
              )}
              <div className="pr-dialog__row pr-dialog__row--total">
                <span className="pr-dialog__total-label">{t('Final Price')}</span>
                <span className="pr-dialog__total-value">{price}</span>
              </div>
            </section>
          </>
        )}

        {error && (
          <p className="pr-dialog__error" role="alert">
            {t(error)}
          </p>
        )}

        <div className="pr-dialog__actions">
          <button
            type="button"
            className="pr-dialog__cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className="pr-dialog__confirm"
            onClick={onConfirm}
            disabled={isSubmitting || !preview}
            aria-busy={isSubmitting || undefined}
          >
            {isSubmitting && <span className="pr-dialog__spinner" aria-hidden="true" />}
            {isSubmitting
              ? t('Redirecting…')
              : isFree
                ? t('Confirm')
                : t('Confirm and pay {{price}}', { price })}
          </button>
        </div>
      </div>
    </Modal>
  );
}
